import curry from 'lodash/curry';
import cloneDeep from 'lodash/cloneDeep';
import Immutable from 'immutable';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { expandField } from 'app/actions/SearchActions';
import AllLanguagesToggle from 'app/components/browse/shared/facets/shared/AllLanguagesToggle';
import { FIELD_ID } from 'app/constants/BrowseConstants';
import {
  makeFieldLabel,
  makeFilterLabel,
  makeGroupLabel,
  mapCatalogSearchFieldsToFacetFields,
  mapQueryStateToCatalogSearchParams
} from 'app/helpers/browse';
import { mapSearchBibToDisplayBib } from 'app/helpers/displayBib';
import { selectBibEntities } from 'app/selectors/EntitiesSelector';
import { selectShowMoreFiltersFieldId } from 'app/selectors/SearchSelector';
import { selectCurrentSiteScopes } from 'app/selectors/ScopeSelector';

const useBrowseFacets = ({
  baseApiParams,
  baseQuerystring,
  deserializeQuerystring,
  includedFieldIds,
  serializeQuerystring
}) => {
  const dispatch = useDispatch();
  const { push } = useHistory();
  const intl = useIntl();
  const { search } = useLocation();
  const searchScopes = useSelector(selectCurrentSiteScopes, Immutable.List())?.toJS();

  const queryState = deserializeQuerystring(search, { includedFieldIds, searchScopes });
  const { appliedFilterIdsByFieldId, isIncludeAllLanguages } = queryState;

  const onToggleAllLanguages = () => {
    const newIsIncludeAllLanguages = !queryState.isIncludeAllLanguages;
    const newAppliedFilterIdsByFieldId = cloneDeep(appliedFilterIdsByFieldId);

    if (newIsIncludeAllLanguages) {
      newAppliedFilterIdsByFieldId[FIELD_ID.LANGUAGE] = newAppliedFilterIdsByFieldId[FIELD_ID.PRIMARY_LANGUAGE];
      newAppliedFilterIdsByFieldId[FIELD_ID.PRIMARY_LANGUAGE] = [];
    } else {
      newAppliedFilterIdsByFieldId[FIELD_ID.PRIMARY_LANGUAGE] = newAppliedFilterIdsByFieldId[FIELD_ID.LANGUAGE];
      newAppliedFilterIdsByFieldId[FIELD_ID.LANGUAGE] = [];
    }

    const newQueryState = {
      appliedFilterIdsByFieldId: newAppliedFilterIdsByFieldId,
      isIncludeAllLanguages: newIsIncludeAllLanguages,
      isLanguageFieldExpanded: false,
      page: queryState.page
    };

    const querystring = serializeQuerystring(newQueryState, { includedFieldIds, searchScopes });

    push(`?${querystring}`);
  };

  const renderAllLanguagesToggle = ({ className }) => {
    return (
      <AllLanguagesToggle className={className} isActive={isIncludeAllLanguages} onChange={onToggleAllLanguages} />
    );
  };

  const catalogSearchFields = useSelector(state => state.getIn(['search', 'catalogSearch', 'fields']));
  const formatFieldLabel = curry(makeFieldLabel)(intl);
  const showMoreFieldId = useSelector(selectShowMoreFiltersFieldId);

  const [facetFields, setFacetFields] = useState([]);
  const [prevSearchState, setPrevSearchState] = useState({
    search: null,
    wasLanguageFieldToggled: false
  });

  useEffect(() => {
    const { search: prevSearch } = prevSearchState;
    if (prevSearch === search) {
      return;
    }

    const { isIncludeAllLanguages: prevIsIncludeAllLanguages } = deserializeQuerystring(prevSearch, {
      includedFieldIds,
      searchScopes
    });

    const wasLanguageFieldToggled = prevIsIncludeAllLanguages !== isIncludeAllLanguages;

    setPrevSearchState({
      search,
      wasLanguageFieldToggled
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevSearchState, search, setPrevSearchState]);

  useEffect(() => {
    setFacetFields(
      mapCatalogSearchFieldsToFacetFields(catalogSearchFields?.toJS(), {
        formatFieldLabel,
        formatFilterLabel: curry(makeFilterLabel)(intl),
        formatGroupLabel: curry(makeGroupLabel)(intl),
        formatShowMoreOverlayTitle: fieldId => {
          const fieldLabel = formatFieldLabel({ fieldId, isPlural: true });
          return intl.formatMessage({ defaultMessage: 'All {fieldLabel}', id: 'all_field_filters' }, { fieldLabel });
        },
        orderedFieldIds: includedFieldIds,
        renderAllLanguagesToggle,
        queryState,
        searchScopes,
        showMoreFieldId,
        wasLanguageFieldToggled: prevSearchState.wasLanguageFieldToggled
      })
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogSearchFields, prevSearchState, setFacetFields]);

  const searchResults = useSelector(state => state.getIn(['search', 'catalogSearch', 'results']))?.toJS() ?? [];
  const searchBibs = useSelector(selectBibEntities);
  const displayBibs = searchResults
    .map(({ representative: metadataId }) => searchBibs.get(metadataId)?.toJS() ?? null)
    .map(mapSearchBibToDisplayBib);

  const isFetchingSearchResults = useSelector(state => state.getIn(['search', 'catalogSearch', 'isFetching'])) ?? false;
  const isFetchingMoreFilters =
    useSelector(state => state.getIn(['search', 'catalogSearch', 'isFetchingShowMoreFilters'])) ?? false;
  const isFetching = isFetchingSearchResults || isFetchingMoreFilters;
  const pagination = useSelector(state => state.getIn(['search', 'catalogSearch', 'pagination']));

  const handleFilterChange = (filter, newIsApplied) => {
    const { fieldId, filterId, isExclusive } = filter;

    const newAppliedFilterIdsByFieldId = cloneDeep(appliedFilterIdsByFieldId);

    const currentFilters = appliedFilterIdsByFieldId[fieldId];
    let newFilters;
    if (newIsApplied && isExclusive) {
      newFilters = [filterId];
    } else if (newIsApplied) {
      newFilters = currentFilters.concat(filterId);
    } else {
      const filterIndex = currentFilters.indexOf(filterId);
      newFilters = currentFilters.slice(0, filterIndex).concat(currentFilters.slice(filterIndex + 1));
    }

    newAppliedFilterIdsByFieldId[fieldId] = newFilters;

    const newQueryState = {
      appliedFilterIdsByFieldId: newAppliedFilterIdsByFieldId,
      isIncludeAllLanguages: queryState.isIncludeAllLanguages,
      isLanguageFieldExpanded: false,
      page: queryState.page
    };

    const querystring = serializeQuerystring(newQueryState, { includedFieldIds, searchScopes });

    push(`?${baseQuerystring.length ? `${baseQuerystring}&` : ''}${querystring}`);
  };

  const setShowMoreFieldId = fieldId => {
    dispatch(
      expandField(fieldId, {
        ...baseApiParams,
        ...mapQueryStateToCatalogSearchParams(queryState, {
          includedFieldIds
        })
      })
    );
  };

  const unapplyAllFilters = () => {
    const newAppliedFilterIdsByFieldId = includedFieldIds.reduce((acc, fieldId) => {
      if (fieldId === FIELD_ID.SCOPE) {
        acc[fieldId] = appliedFilterIdsByFieldId[fieldId];
      } else {
        acc[fieldId] = [];
      }

      return acc;
    }, {});

    const newQueryState = {
      appliedFilterIdsByFieldId: newAppliedFilterIdsByFieldId,
      isIncludeAllLanguages: queryState.isIncludeAllLanguages,
      isLanguageFieldExpanded: false,
      page: 1
    };

    const querystring = serializeQuerystring(newQueryState, { includedFieldIds, searchScopes });

    push(`?${baseQuerystring.length ? `${baseQuerystring}&` : ''}${querystring}`);
  };

  return {
    displayBibs,
    facetFields,
    handleFilterChange,
    isFetching,
    pagination,
    setShowMoreFieldId,
    unapplyAllFilters
  };
};

export default useBrowseFacets;
